/**
 * ReadinessWizard — ISO 27001 Foundational Readiness Exam Wizard
 * Section-by-section question flow with autosave.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { readinessApi } from '../services/api';
import { useSwal } from '../hooks/useSwal';
import { useToast } from '../hooks/useToast';
import { PageHeader } from '../components/PageHeader';
import { WizardShell, WizardStepFooter } from '../components/WizardShell';
import { useWizardSteps } from '../hooks/useWizardSteps';

const SECTION_ICONS: Record<string, string> = {
  'leadership': 'ri-user-star-line',
  'planning': 'ri-map-2-line',
  'support': 'ri-group-line',
  'operation': 'ri-settings-3-line',
  'performance': 'ri-bar-chart-grouped-line',
  'improvement': 'ri-refresh-line',
  'risk': 'ri-alarm-warning-line',
  'context': 'ri-building-4-line',
};

const getSectionIcon = (sectionName: string) => {
  const key = Object.keys(SECTION_ICONS).find(k => sectionName.toLowerCase().includes(k));
  return key ? SECTION_ICONS[key] : 'ri-checkbox-multiple-line';
};

const SCORE_STYLES: Record<number, { colour: string; icon: string }> = {
  0: { colour: 'danger',    icon: 'ri-close-circle-line' },
  1: { colour: 'warning',   icon: 'ri-error-warning-line' },
  2: { colour: 'secondary', icon: 'ri-minus-circle-line' },
  3: { colour: 'info',      icon: 'ri-information-line' },
  4: { colour: 'primary',   icon: 'ri-checkbox-circle-line' },
  5: { colour: 'success',   icon: 'ri-shield-check-line' },
};

type QuestionOption = { score: number; label: string; description: string };
type Question = {
  questionId: string; section: string; sectionId: string; clause: string;
  weight: number; text: string; guidance: string; consultantNote?: string; options: QuestionOption[];
  isCeilingControl?: boolean;
};
type Response = { score: number; comment: string };

export const ReadinessWizard: React.FC = () => {
  const { assessmentId } = useParams<{ assessmentId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const swal = useSwal();
  const toast = useToast();

  const [responses, setResponses] = useState<Record<string, Response>>({});
  const [savingStatus, setSavingStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [expandedNotes, setExpandedNotes] = useState<Set<string>>(new Set());
  const toggleNote = (qid: string) => setExpandedNotes(prev => {
    const next = new Set(prev);
    next.has(qid) ? next.delete(qid) : next.add(qid);
    return next;
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['readiness-assessment', assessmentId],
    queryFn: () => readinessApi.get(assessmentId!),
    enabled: !!assessmentId,
  });

  const assessment = data?.assessment;
  const questions: Question[] = assessment?.questions || [];

  const sections = useMemo(() => {
    const map: Record<string, { sectionId: string; section: string; questions: Question[] }> = {};
    for (const q of questions) {
      if (!map[q.sectionId]) map[q.sectionId] = { sectionId: q.sectionId, section: q.section, questions: [] };
      map[q.sectionId].questions.push(q);
    }
    return Object.values(map).sort((a, b) => a.sectionId.localeCompare(b.sectionId));
  }, [questions]);

  useEffect(() => {
    if (assessment?.responses) {
      const loaded: Record<string, Response> = {};
      for (const [qid, val] of Object.entries(assessment.responses as Record<string, any>)) {
        loaded[qid] = { score: Number(val.score ?? 0), comment: val.comment ?? '' };
      }
      setResponses(loaded);
    }
  }, [assessment?.responses]);

  const saveMutation = useMutation({
    mutationFn: (data: { responses: Record<string, any> }) => readinessApi.update(assessmentId!, data),
    onMutate: () => setSavingStatus('saving'),
    onSuccess: () => { setSavingStatus('saved'); setTimeout(() => setSavingStatus('idle'), 2000); },
    onError: () => setSavingStatus('error'),
  });

  const submitMutation = useMutation({
    mutationFn: (data: { responses: Record<string, any> }) => readinessApi.submit(assessmentId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['readiness-assessments'] });
      navigate(`/readiness/${assessmentId}/results`);
    },
    onError: () => toast.error('Failed to submit assessment. Please try again.'),
  });

  const handleSave = useCallback(() => { saveMutation.mutate({ responses }); }, [responses, saveMutation]);

  const handleResponseChange = (questionId: string, score: number) => {
    setResponses(prev => ({ ...prev, [questionId]: { ...prev[questionId], score, comment: prev[questionId]?.comment ?? '' } }));
  };
  const handleCommentChange = (questionId: string, comment: string) => {
    setResponses(prev => ({ ...prev, [questionId]: { ...prev[questionId], comment, score: prev[questionId]?.score ?? 0 } }));
  };

  const handleSubmit = async () => {
    const confirmed = await swal.confirm('Submit assessment?', 'This will calculate your readiness score and lock the responses.');
    if (confirmed) submitMutation.mutate({ responses });
  };

  const { currentStepIndex: currentSectionIdx, goTo: setCurrentSectionIdx, goNext, goBack, isFirst, isLast } = useWizardSteps(sections.length);
  const currentSection = sections[currentSectionIdx];
  const sectionAnswered = (idx: number) => sections[idx]?.questions.filter(q => responses[q.questionId] != null).length ?? 0;
  const totalAnswered = questions.filter(q => responses[q.questionId] != null).length;
  const totalProgress = questions.length > 0 ? Math.round((totalAnswered / questions.length) * 100) : 0;

  if (isLoading) {
    return (
      <div className="container-xxl flex-grow-1 container-p-y text-center py-5">
        <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading…</span></div>
      </div>
    );
  }
  if (error || !assessment) {
    return (
      <div className="container-xxl flex-grow-1 container-p-y">
        <div className="np-banner np-tone-danger"><i className="ri-error-warning-line"></i>Assessment not found or could not be loaded.</div>
      </div>
    );
  }
  if (assessment.status === 'completed') {
    return (
      <div className="container-xxl flex-grow-1 container-p-y text-center py-5">
        <span className="np-icon-badge-lg np-tone-success mx-auto mb-3">
          <i className="ri-checkbox-circle-fill"></i>
        </span>
        <h5>This assessment has already been submitted.</h5>
        <a href={`/readiness/${assessmentId}/results`} className="np-btn np-btn-primary mt-2">
          <i className="ri-bar-chart-line"></i>View Results
        </a>
      </div>
    );
  }

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <PageHeader
        icon="ri-award-line"
        title={assessment.title}
        colour="success"
        breadcrumbs={[{ label: 'ISO 27001 Readiness', to: '/readiness' }, { label: 'Assessment' }]}
        actions={
          <>
            {savingStatus === 'saving' && <span className="np-sub"><span className="spinner-border spinner-border-sm me-1"></span>Saving…</span>}
            {savingStatus === 'saved' && <span className="np-sub text-success"><i className="ri-check-line me-1"></i>Saved</span>}
            {savingStatus === 'error' && <span className="np-sub text-danger"><i className="ri-error-warning-line me-1"></i>Save failed</span>}
            <button className="np-btn np-btn-ghost np-btn-sm" onClick={handleSave} disabled={saveMutation.isPending}>
              <i className="ri-save-line"></i>Save
            </button>
          </>
        }
      />

      {/* Overall progress */}
      <div className="np-card">
        <div className="np-card-body py-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div className="d-flex align-items-center gap-2">
              <i className="ri-task-line text-primary"></i>
              <span className="fw-semibold small">Overall Progress</span>
            </div>
            <span className="fw-bold text-primary np-tabular">{totalProgress}%</span>
          </div>
          <div className="np-progress">
            <div className="np-fill" style={{ width: `${totalProgress}%` }} />
          </div>
          <div className="np-hint">{totalAnswered} of {questions.length} questions answered</div>
        </div>
      </div>

      <WizardShell
        sidebarTitle="Sections"
        steps={sections.map((sec, idx) => {
          const answered = sectionAnswered(idx);
          const total = sec.questions.length;
          return {
            id: sec.sectionId,
            label: sec.section,
            icon: getSectionIcon(sec.section),
            progressPct: total > 0 ? Math.round((answered / total) * 100) : 0,
            done: answered === total,
            meta: `${answered}/${total}`,
          };
        })}
        currentStepIndex={currentSectionIdx}
        onStepSelect={setCurrentSectionIdx}
      >
        {(() => {
          if (!currentSection) return null;
          return (
            <>
              {/* Section header */}
              <div className="np-card np-card-accent-primary mb-4">
                <div className="np-card-body py-3">
                  <div className="d-flex align-items-center gap-3">
                    <span className="np-icon-badge np-tone-primary flex-shrink-0">
                      <i className={getSectionIcon(currentSection.section)}></i>
                    </span>
                    <div>
                      <h5 className="mb-0">{currentSection.section}</h5>
                      <p className="np-sub mb-0">
                        Clauses: {currentSection.questions.map(q => q.clause).join(', ')}
                        &nbsp;·&nbsp; {sectionAnswered(currentSectionIdx)}/{currentSection.questions.length} answered
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {currentSection.questions.map((q, qIdx) => {
                const resp = responses[q.questionId];
                const selectedScore = resp?.score ?? -1;

                return (
                  <div key={q.questionId} className="np-card mb-4">
                    <div className="np-card-body">
                      <div className="d-flex align-items-start justify-content-between gap-2 flex-wrap mb-3">
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <span className="np-chip np-tone-neutral">{q.questionId}</span>
                          <span className="np-chip np-tone-neutral">Clause {q.clause}</span>
                          {q.isCeilingControl && (
                            <span className="np-chip np-tone-danger">
                              <i className="ri-error-warning-line"></i>Critical Control
                            </span>
                          )}
                        </div>
                        <span className="np-hint m-0">Q{qIdx + 1}/{currentSection.questions.length}</span>
                      </div>

                      <p className="fw-semibold mb-1">{q.text}</p>
                      <p className="np-sub mb-2">
                        <i className="ri-lightbulb-line me-1 text-warning"></i>{q.guidance}
                      </p>

                      {q.consultantNote && (
                        <div className="mb-4">
                          <button
                            className="np-btn np-btn-ghost np-btn-sm"
                            onClick={() => toggleNote(q.questionId)}
                          >
                            <i className={`ri-${expandedNotes.has(q.questionId) ? 'subtract' : 'add'}-line`}></i>
                            Consultant Insight
                          </button>
                          {expandedNotes.has(q.questionId) && (
                            <div className="np-banner np-tone-info mt-2 mb-0">
                              <i className="ri-user-star-line"></i>
                              <span>{q.consultantNote}</span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Score options */}
                      <div className="np-choice-list mb-3">
                        {q.options.map((opt) => {
                          const isSelected = selectedScore === opt.score;
                          const style = SCORE_STYLES[opt.score] ?? { colour: 'secondary', icon: 'ri-circle-line' };
                          const tone = style.colour === 'secondary' ? 'neutral' : style.colour;
                          return (
                            <button
                              key={opt.score}
                              className={`np-choice ${isSelected ? `selected np-tone-${tone}` : ''}`}
                              onClick={() => handleResponseChange(q.questionId, opt.score)}
                            >
                              <i className={`${style.icon} text-${style.colour}`}></i>
                              <div className="np-choice-body">
                                <div className="d-flex align-items-center gap-2">
                                  <span className="np-choice-label">{opt.score} — {opt.label}</span>
                                  {isSelected && <i className="ri-checkbox-circle-fill ms-auto text-success"></i>}
                                </div>
                                <p className="np-choice-desc mb-0">{opt.description}</p>
                              </div>
                            </button>
                          );
                        })}
                      </div>

                      <textarea
                        className="np-textarea"
                        rows={2}
                        placeholder="Notes / evidence reference (optional)"
                        value={resp?.comment ?? ''}
                        onChange={(e) => handleCommentChange(q.questionId, e.target.value)}
                      />
                    </div>
                  </div>
                );
              })}

              {/* Section navigation */}
              <WizardStepFooter onBack={goBack} backDisabled={isFirst}>
                {!isLast ? (
                  <button
                    className="np-btn np-btn-primary"
                    onClick={() => { handleSave(); goNext(); }}
                  >
                    Save &amp; Next<i className="ri-arrow-right-line"></i>
                  </button>
                ) : (
                  <div className="text-end">
                    {totalAnswered < questions.length && (
                      <div className="text-warning small mb-2">
                        <i className="ri-error-warning-line me-1"></i>
                        {questions.length - totalAnswered} question{questions.length - totalAnswered !== 1 ? 's' : ''} remaining
                      </div>
                    )}
                    <button
                      className="np-btn np-btn-success"
                      onClick={handleSubmit}
                      disabled={submitMutation.isPending || totalAnswered < questions.length}
                    >
                      {submitMutation.isPending
                        ? <><span className="spinner-border spinner-border-sm me-2"></span>Submitting…</>
                        : <><i className="ri-send-plane-line"></i>Submit Assessment ({totalAnswered}/{questions.length})</>}
                    </button>
                  </div>
                )}
              </WizardStepFooter>
            </>
          );
        })()}
      </WizardShell>
    </div>
  );
};
